import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Divider,
  Alert
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import ShareIcon from '@mui/icons-material/Share';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

// Hooks
import { useHighRiskForm } from './hooks/useHighRiskForm';

// Components
import SignatureDialog from './components/SignatureDialog';

// Screens
import DemographicsSection from './screens/DemographicsSection';
import RiskAssessmentSection from './screens/RiskAssessmentSection';
import HighRiskSignaturesSection from './screens/HighRiskSignaturesSection';
import SurrogateSection from './screens/SurrogateSection';

const HighRiskConsentForm = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData,
    setFormData,
    errors,
    loading,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare,
    isExistingRecord
  } = useHighRiskForm(uhid_no, navigate);

  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = () => {
    navigate(`/video?form=${encodeURIComponent("CONSENT FOR HIGH RISK")}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };

  if (loading && !formData.patient_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, border: '1px solid #e2e8f0' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Button
                onClick={handleBack}
                startIcon={<span>←</span>}
                sx={{ color: '#008080', fontWeight: 600, mb: 1, textTransform: 'none' }}
              >
                Back
              </Button>
              <Typography
                variant="h4"
                sx={{
                  color: '#008080',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 1
                }}
              >
                Consent for High Risk
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: '32px', height: '32px' }} />
              <Typography variant="h6" sx={{ color: '#008080', fontWeight: 600 }}>OTMS</Typography>
            </Box>
          </Box>

          {/* Action Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ bgcolor: '#008080', textTransform: 'none' }}
              >
                Home
              </Button>
              <Button
                variant="outlined"
                startIcon={<VideoCallIcon />}
                onClick={goToVideoRecorder}
                sx={{ color: '#008080', borderColor: '#008080', textTransform: 'none' }}
              >
                Add Iterative Video
              </Button>
            </Box>
            <IconButton color="primary" onClick={handleShare} sx={{ border: '1px solid #008080' }}>
              <ShareIcon />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit}>
            <DemographicsSection 
              formData={formData} 
              handleChange={handleChange} 
              errors={errors} 
            />

            <Divider sx={{ my: 4 }} />

            <RiskAssessmentSection 
              formData={formData} 
              handleChange={handleChange} 
              setFormData={setFormData}
              errors={errors} 
            />

            <Divider sx={{ my: 4 }} />

            <HighRiskSignaturesSection 
              formData={formData} 
              handleSignatureOpen={handleSignatureOpen}
              handleNameChange={handleNameChange}
              handleChange={handleChange}
            />

            <SurrogateSection 
              formData={formData} 
              handleChange={handleChange} 
              handleNameChange={handleNameChange}
              handleSignatureOpen={handleSignatureOpen}
              setFormData={setFormData}
            />

            <Box sx={{ display: 'flex', justifyContent: isExistingRecord ? 'center' : 'flex-start', gap: 2, mt: 6 }}>
              {!isExistingRecord && (
                <DraftSaveButton
                  formKey={DRAFT_KEYS.HIGH_RISK_CONSENT}
                  uhidNo={uhid_no}
                  formData={formData}
                  onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                  variant="outlined"
                  size="large"
                  isUpdate={false}
                />
              )}
              <Button
                type="submit"
                variant="contained"
                fullWidth={!isExistingRecord}
                sx={{
                  bgcolor: '#008080',
                  '&:hover': { bgcolor: '#006666' },
                  borderRadius: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  ...(isExistingRecord && { minWidth: 320, px: 6, py: 1.5 })
                }}
              >
                {isExistingRecord ? "Update High Risk Consent" : "Submit High Risk Consent"}
              </Button>
            </Box>
          </form>

          {/* Dialogs */}
          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default HighRiskConsentForm;
